import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import PageHeader from '../components/PageHeader';
import AccuracyBadge from '../components/AccuracyBadge';
import LocationAutocomplete from '../components/LocationAutocomplete';
import { getVehiclesForMarket } from '../lib/supabase';
import { formatPressure, kpaToBar, kpaToPsi, barToKpa, psiToKpa } from '../lib/locale';

// ----- Local date/time helpers for v2 form fields ----------------------------
// `<input type="date">` and `<input type="datetime-local">` use local-time
// strings without timezone (YYYY-MM-DD and YYYY-MM-DDTHH:MM). Convert to/from
// these formats in the user's local timezone — same convention the platform
// uses for those input types.
function pad2(n) { return String(n).padStart(2, '0'); }

function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function isoNowLocalForInput() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
         `T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
// -----------------------------------------------------------------------------

export default function PlanPage() {
  const navigate = useNavigate();
  const { locale, countryCode } = useLocale();
  
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  // The structured location captured from the autocomplete dropdown.
  // When set, we use this directly (no second geocode round-trip).
  // When null (user typed but hasn't selected), submit is blocked.
  const [originLocation, setOriginLocation] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);
  
  const [startSoc, setStartSoc] = useState(90);
  const [arriveSoc, setArriveSoc] = useState(20);
  const [maxSoc, setMaxSoc] = useState(80);
  
  const [climateMode, setClimateMode] = useState('on');
  const [drivingStyle, setDrivingStyle] = useState('normal');
  const [roadQuality, setRoadQuality] = useState('good');
  
  const [tirePressureBar, setTirePressureBar] = useState(2.4);
  const [drivetrainMode, setDrivetrainMode] = useState('auto');

  // ---------- v2 tire pressure / departure context (Task E1) -------------
  // All optional. When `tireCheckDate` is set, the engine applies the
  // permeation model from tirePressureV2.js. When `departure` is set, the
  // weather client fetches the forecast for that hour instead of "now".
  // Defaults: tire check today, air (most common), depart now.
  const [tireCheckDate, setTireCheckDate] = useState(() => isoToday());
  const [inflationGas, setInflationGas] = useState('air');
  const [departure, setDeparture] = useState(() => isoNowLocalForInput());
  // -----------------------------------------------------------------------
  
  useEffect(() => {
    async function load() {
      try {
        setLoadingVehicles(true);
        const data = await getVehiclesForMarket(countryCode);
        setVehicles(data || []);
        setLoadingVehicles(false);
      } catch (e) {
        setError(`Could not load vehicles: ${e.message}`);
        setLoadingVehicles(false);
      }
    }
    load();
  }, [countryCode]);
  
  const selectedVehicle = useMemo(
    () => vehicles.find(v => v.vehicle_id === selectedVehicleId),
    [vehicles, selectedVehicleId]
  );
  
  useEffect(() => {
    if (!selectedVehicle?.tire_pressure_spec_kpa) return;
    setTirePressureBar(Number((selectedVehicle.tire_pressure_spec_kpa / 100).toFixed(1)));
  }, [selectedVehicle]);
  
  const tirePressureDisplay = useMemo(() => {
    const kpa = barToKpa(tirePressureBar);
    return formatPressure(kpa, locale);
  }, [tirePressureBar, locale]);
  
  const handleTirePressureChange = (e) => {
    const v = parseFloat(e.target.value);
    if (locale.pressure_unit === 'psi') {
      setTirePressureBar(Number((psiToKpa(v) / 100).toFixed(2)));
    } else {
      setTirePressureBar(v);
    }
  };
  
  const tirePressureSliderValue = locale.pressure_unit === 'psi' 
    ? Math.round(kpaToPsi(tirePressureBar * 100))
    : tirePressureBar;
  
  // canPlan requires the user to have actually SELECTED a location from the
  // autocomplete dropdown — not just typed text. This is what prevents silent
  // typo-routing: if the user types "Mumbi" and never picks anything, the
  // button stays disabled and they can see they haven't disambiguated.
  const canPlan = selectedVehicleId
    && originLocation && destinationLocation
    && !loadingVehicles;
  
  function handlePlanTrip() {
    const tripId = `trip-${Date.now()}`;
    const params = new URLSearchParams({
      vehicle_id: selectedVehicleId,
      origin: origin.trim(),
      destination: destination.trim(),
      // Structured selection from the autocomplete — these let the orchestrator
      // skip the re-geocoding step and avoid silent typo-routing. The plain
      // origin/destination text above is retained for display only.
      origin_lat: String(originLocation.lat),
      origin_lng: String(originLocation.lng),
      origin_cc: originLocation.country_code || '',
      dest_lat: String(destinationLocation.lat),
      dest_lng: String(destinationLocation.lng),
      dest_cc: destinationLocation.country_code || '',
      start_soc: startSoc,
      arrive_soc: arriveSoc,
      max_soc: maxSoc,
      climate: climateMode,
      style: drivingStyle,
      road_quality: roadQuality,
      tire_pressure_kpa: Math.round(tirePressureBar * 100),
      drivetrain_mode: drivetrainMode,
      // v2 tire pressure / departure context — opt-in. The orchestrator
      // converts tire_check_date to days_since_tire_check and uses
      // departure as the weather forecast time.
      tire_check_date: tireCheckDate,
      inflation_gas: inflationGas,
      departure: departure,
    });
    navigate(`/result/${tripId}?${params.toString()}`);
  }
  
  return (
    <div className="page">
      <PageHeader title="Plan a trip" />
      
      {error && <div className="error-banner">{error}</div>}
      
      <p className="label">Your vehicle</p>
      <div className="field">
        <span className="field-icon">⏵</span>
        <select
          value={selectedVehicleId}
          onChange={(e) => setSelectedVehicleId(e.target.value)}
          disabled={loadingVehicles}
        >
          <option value="">
            {loadingVehicles ? 'Loading vehicles…' : `Select your EV (${vehicles.length} available)`}
          </option>
          {vehicles.map(v => (
            <option key={v.vehicle_id} value={v.vehicle_id}>
              {v.brand} {v.model} {v.year_range ? `(${v.year_range})` : ''}
              {v.battery_kwh ? ` · ${v.battery_kwh} kWh` : ''}
            </option>
          ))}
        </select>
      </div>
      
      {selectedVehicle && (
        <AccuracyBadge 
          vehicle={selectedVehicle} 
          locale={locale}
          onShopAccessories={() => navigate(`/accessories?vehicle=${selectedVehicleId}`)}
        />
      )}
      
      <p className="label">Route</p>
      <div style={{ marginBottom: 8 }}>
        <LocationAutocomplete
          value={origin}
          onChange={setOrigin}
          onSelect={setOriginLocation}
          placeholder="Origin (city or address)"
          showCurrentLocation={true}
        />
      </div>
      <div style={{ marginBottom: 8 }}>
        <LocationAutocomplete
          value={destination}
          onChange={setDestination}
          onSelect={setDestinationLocation}
          placeholder="Destination"
          iconColor="red"
        />
      </div>
      {(origin.trim() && !originLocation) || (destination.trim() && !destinationLocation) ? (
        <p style={{ fontSize: 10, color: 'var(--accent-warning)', margin: '-4px 0 8px 4px' }}>
          Pick a result from the dropdown to disambiguate the location
        </p>
      ) : null}

      <p className="label">Departure</p>
      <div className="field">
        <span className="field-icon">◷</span>
        <input
          type="datetime-local"
          value={departure}
          onChange={(e) => setDeparture(e.target.value)}
        />
      </div>
      <p style={{ fontSize: 10, color: 'var(--text-tertiary)', margin: '-4px 0 8px 4px' }}>
        Weather forecast and tire warm-up use this time
      </p>
      
      <p className="label">Battery</p>
      <div className="slider-block">
        <div className="slider-label">
          <span>Starting SoC</span>
          <span className="value">{startSoc}%</span>
        </div>
        <input
          type="range"
          min="10"
          max="100"
          step="1"
          value={startSoc}
          onChange={(e) => setStartSoc(parseInt(e.target.value))}
        />
      </div>
      <div className="slider-block">
        <div className="slider-label">
          <span>Arrive with at least</span>
          <span className="value">{arriveSoc}%</span>
        </div>
        <input
          type="range"
          min="5"
          max="50"
          step="1"
          value={arriveSoc}
          onChange={(e) => setArriveSoc(parseInt(e.target.value))}
        />
      </div>
      <div className="slider-block">
        <div className="slider-label">
          <span>Max SoC during charging</span>
          <span className="value">{maxSoc}%</span>
        </div>
        <input
          type="range"
          min="50"
          max="100"
          step="5"
          value={maxSoc}
          onChange={(e) => setMaxSoc(parseInt(e.target.value))}
        />
      </div>
      
      <p className="label">Climate</p>
      <div className="chip-row">
        <button className={`chip ${climateMode === 'off' ? 'active' : ''}`} onClick={() => setClimateMode('off')}>Off</button>
        <button className={`chip ${climateMode === 'on' ? 'active' : ''}`} onClick={() => setClimateMode('on')}>On</button>
        <button className={`chip ${climateMode === 'eco' ? 'active' : ''}`} onClick={() => setClimateMode('eco')}>Eco</button>
      </div>
      
      <p className="label">Driving style</p>
      <div className="chip-row">
        <button className={`chip ${drivingStyle === 'efficient' ? 'active' : ''}`} onClick={() => setDrivingStyle('efficient')}>Efficient</button>
        <button className={`chip ${drivingStyle === 'normal' ? 'active' : ''}`} onClick={() => setDrivingStyle('normal')}>Normal</button>
        <button className={`chip ${drivingStyle === 'spirited' ? 'active' : ''}`} onClick={() => setDrivingStyle('spirited')}>Spirited</button>
      </div>
      
      <p className="label">Road quality</p>
      <div className="chip-row">
        <button className={`chip ${roadQuality === 'highway' ? 'active' : ''}`} onClick={() => setRoadQuality('highway')}>Highway</button>
        <button className={`chip ${roadQuality === 'good' ? 'active' : ''}`} onClick={() => setRoadQuality('good')}>Good</button>
        <button className={`chip ${roadQuality === 'rough' ? 'active' : ''}`} onClick={() => setRoadQuality('rough')}>Rough</button>
      </div>
      
      <p className="label">Tire pressure ({tirePressureDisplay})</p>
      <div className="slider-block">
        <div className="slider-label">
          <span>Current pressure ({locale.pressure_unit})</span>
          <span className="value">{tirePressureDisplay}</span>
        </div>
        {locale.pressure_unit === 'psi' ? (
          <input type="range" min="20" max="50" step="1" value={tirePressureSliderValue} onChange={handleTirePressureChange} />
        ) : (
          <input type="range" min="1.5" max="3.5" step="0.1" value={tirePressureSliderValue} onChange={handleTirePressureChange} />
        )}
      </div>
      {selectedVehicle?.tire_pressure_spec_kpa && (
        <p style={{ fontSize: 10, color: 'var(--text-tertiary)', margin: '-4px 0 8px 4px' }}>
          OEM spec: {formatPressure(selectedVehicle.tire_pressure_spec_kpa, locale)}
        </p>
      )}

      <div className="field">
        <span className="field-icon">⏱</span>
        <input
          type="date"
          value={tireCheckDate}
          onChange={(e) => setTireCheckDate(e.target.value)}
          max={isoToday()}
        />
      </div>
      <p style={{ fontSize: 10, color: 'var(--text-tertiary)', margin: '-4px 0 8px 4px' }}>
        When did you last check tire pressure? Tires lose pressure over time
        (~10 kPa/month for air, ~6.7 kPa/month for nitrogen).
      </p>

      <p className="label" style={{ fontSize: 11 }}>Inflation gas</p>
      <div className="chip-row">
        <button
          className={`chip ${inflationGas === 'air' ? 'active' : ''}`}
          onClick={() => setInflationGas('air')}
        >Air</button>
        <button
          className={`chip ${inflationGas === 'n2' ? 'active' : ''}`}
          onClick={() => setInflationGas('n2')}
        >Nitrogen (N₂)</button>
      </div>
      
      {selectedVehicle?.drivetrain_decouple && (
        <>
          <p className="label">Drivetrain</p>
          <div className="chip-row">
            <button className={`chip ${drivetrainMode === 'auto' ? 'active' : ''}`} onClick={() => setDrivetrainMode('auto')}>Auto</button>
            <button className={`chip ${drivetrainMode === 'rwd' ? 'active' : ''}`} onClick={() => setDrivetrainMode('rwd')}>RWD only</button>
            <button className={`chip ${drivetrainMode === 'awd' ? 'active' : ''}`} onClick={() => setDrivetrainMode('awd')}>AWD always</button>
          </div>
          <p style={{ fontSize: 10, color: 'var(--text-tertiary)', margin: '-4px 0 8px 4px' }}>
            Your vehicle can decouple drivetrains for efficiency
          </p>
        </>
      )}
      
      <button className="btn-primary" onClick={handlePlanTrip} disabled={!canPlan}>
        Plan my trip
      </button>
    </div>
  );
}
